import { useState, useRef, useEffect } from 'react'
import { motion } from 'framer-motion'
import { RefreshCw, ArrowRight, Loader2 } from 'lucide-react'
import { useAuthStore } from '../../stores/authStore'

interface VerifyEmailStepProps {
  onNext: () => void
}

/**
 * VerifyEmailStep - Minimal email verification
 *
 * "Details matter, it's worth waiting to get it right." — Steve Jobs
 */

export default function VerifyEmailStep({ onNext }: VerifyEmailStepProps) {
  const [code, setCode] = useState(['', '', '', '', '', ''])
  const [resendCooldown, setResendCooldown] = useState(0)
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  const { verifyEmail, startRegistration, onboarding, isLoading, error, clearError } = useAuthStore()

  // Handle cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000)
      return () => clearTimeout(timer)
    }
  }, [resendCooldown])

  // Focus first input on mount
  useEffect(() => {
    inputRefs.current[0]?.focus()
  }, [])

  const handleChange = (index: number, value: string) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return

    // Clear error when typing
    if (error) clearError()

    const newCode = [...code]
    newCode[index] = value
    setCode(newCode)

    // Auto-advance to next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    // Handle backspace
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)

    if (pastedData) {
      const newCode = [...code]
      pastedData.split('').forEach((char, i) => {
        if (i < 6) newCode[i] = char
      })
      setCode(newCode)

      // Focus last filled input or the next empty one
      const lastIndex = Math.min(pastedData.length, 5)
      inputRefs.current[lastIndex]?.focus()
    }
  }

  const handleResend = async () => {
    if (resendCooldown > 0 || !onboarding.pendingEmail) return

    try {
      await startRegistration(onboarding.pendingEmail)
      setResendCooldown(60) // 60 second cooldown
      setCode(['', '', '', '', '', ''])
      inputRefs.current[0]?.focus()
    } catch {
      // Error handled by store
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const fullCode = code.join('')
    if (fullCode.length !== 6) return

    try {
      await verifyEmail(fullCode)
      onNext()
    } catch {
      // Error handled by store
    }
  }

  const isComplete = code.every((digit) => digit !== '')

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="space-y-8"
    >
      {/* Header */}
      <div className="space-y-3">
        <h2 className="text-3xl font-extralight tracking-tight text-white">
          Verify your email
        </h2>
        <p className="text-white/40 font-light">
          Enter the code we sent to
        </p>
        <p className="font-mono text-white/60 text-sm">
          {onboarding.pendingEmail || 'your email'}
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* 6-digit code input */}
        <div className="space-y-4">
          <div className="flex justify-center gap-3">
            {code.map((digit, index) => (
              <motion.input
                key={index}
                ref={(el) => {
                  inputRefs.current[index] = el
                }}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={index === 0 ? handlePaste : undefined}
                disabled={isLoading}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: index * 0.05 }}
                className={`
                  w-12 h-14 text-center text-xl font-mono
                  bg-transparent border-b-2
                  text-white
                  focus:outline-none
                  disabled:opacity-30 disabled:cursor-not-allowed
                  transition-colors duration-300
                  ${digit ? 'border-white/40' : 'border-white/10'}
                  ${error ? 'border-white/30' : ''}
                  focus:border-white/60
                `}
              />
            ))}
          </div>

          {/* Error message */}
          {error && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-sm text-white/40 text-center"
            >
              {error}
            </motion.p>
          )}
        </div>

        {/* Security note */}
        <p className="text-xs text-white/30 font-light text-center">
          Code expires in 10 minutes
        </p>

        {/* Submit button */}
        <button
          type="submit"
          disabled={!isComplete || isLoading}
          className="
            w-full py-4
            bg-white text-black text-sm font-medium tracking-wide
            disabled:opacity-20 disabled:cursor-not-allowed
            hover:bg-white/90 active:bg-white/80
            transition-all duration-200
            flex items-center justify-center gap-3
          "
        >
          {isLoading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <>
              <span>Verify</span>
              <ArrowRight className="w-4 h-4" />
            </>
          )}
        </button>

        {/* Resend button */}
        <div className="text-center">
          <button
            type="button"
            onClick={handleResend}
            disabled={resendCooldown > 0 || isLoading}
            className="
              text-xs text-white/30 hover:text-white/50
              disabled:opacity-30 disabled:cursor-not-allowed
              transition-colors duration-300 inline-flex items-center gap-2
            "
          >
            <RefreshCw className="w-3 h-3" />
            {resendCooldown > 0
              ? `Resend in ${resendCooldown}s`
              : 'Resend code'
            }
          </button>
        </div>
      </form>

      {/* Check spam note */}
      <p className="text-center text-[10px] text-white/20">
        Check your spam folder if you don't see it
      </p>
    </motion.div>
  )
}
